import React, { useEffect, useState } from "react";
import { Header } from "../components/Header";
import { FileUploaderWithPreview } from "../components/FileUploaderWithPreview";
import { useNavigate } from "react-router-dom";
import { KnowledgeImporter } from "../components/KnowledgeImporter";
import {
  Database,
  FileText,
  FolderOpen,
  Search,
  Download,
  Upload,
  FileJson,
  Trash2,
  RefreshCw,
  ChevronRight,
  Clock,
  Tag,
  Archive,
  Eye,
  AlertCircle,
  CheckCircle,
  XCircle,
  PieChart,
  List,
  Grid,
  Plus,
  RotateCcw,
  Save,
} from "lucide-react";
import axios from "axios";
import { API_ORIGIN } from "../config/api";
import { useLanguage } from "../i18n/LanguageContext";

interface KnowledgeStats {
  totalDocuments: number;
  categories: string[];
  categoryStats: Record<string, { count: number; documents: any[] }>;
  uploadedFiles: {
    count: number;
    files: Array<{
      name: string;
      size: number;
      created: string;
    }>;
  };
  lastUpdated: string;
}

interface Backup {
  id: string;
  name: string;
  createdAt: string;
  stats: {
    documentCount: number;
    categories: string[];
  };
  sizeFormatted: string;
  path?: string;
}

interface Document {
  id: string;
  title: string;
  category: string;
  content: string;
  preview: string;
  score?: number;
  metadata?: any;
  timestamp?: string;
}

const KnowledgeBasePage: React.FC = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [activeTab, setActiveTab] = useState<
    "overview" | "documents" | "upload" | "search" | "backup"
  >("overview");
  const [stats, setStats] = useState<KnowledgeStats | null>(null);
  const [backups, setBackups] = useState<Backup[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Document[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [documentQuery, setDocumentQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const [editableDoc, setEditableDoc] = useState<Document | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [showImporter, setShowImporter] = useState(false);
  const [savingDoc, setSavingDoc] = useState(false);

  const API_BASE_URL = API_ORIGIN;

  // 加载概览数据
  const loadOverview = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/knowledge/overview`
      );
      setStats(response.data);
    } catch (err) {
      setError(t("加载知识库失败", "Failed to load knowledge base"));
    } finally {
      setLoading(false);
    }
  };

  // 加载备份列表
  const loadBackups = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/knowledge/backup/list`
      );
      setBackups(response.data.backups || []);
    } catch (err) {
      setError(t("加载备份列表失败", "Failed to load backup list"));
    }
  };

  // 搜索知识库
  const searchKnowledge = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/knowledge/search`,
        {
          query: searchQuery,
          limit: 20,
        }
      );
      setSearchResults(response.data.results || []);
    } catch (err) {
      setError(t("搜索失败", "Search failed"));
    } finally {
      setLoading(false);
    }
  };

  // 加载文档列表
  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/knowledge/documents`,
        {
          params: {
            query: documentQuery || undefined,
            category: categoryFilter || undefined,
            limit: 200,
          },
        }
      );
      setDocuments(response.data.documents || []);
    } catch (err) {
      setError(t("加载文档列表失败", "Failed to load documents"));
    } finally {
      setLoading(false);
    }
  };

  // 创建备份
  const createBackup = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/knowledge/backup/create`,
        {
          includeFiles: true,
        }
      );
      setSuccess(t(`备份创建成功: ${response.data.backupName}`, `Backup created: ${response.data.backupName}`));
      loadBackups();
    } catch (err) {
      setError(t("创建备份失败", "Failed to create backup"));
    }
  };

  // 恢复备份
  const restoreBackup = async (backupId: string) => {
    if (!window.confirm(t("确定要恢复这个备份吗？当前知识库将被替换。", "Restore this backup? The current knowledge base will be replaced."))) {
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/knowledge/backup/restore/${backupId}`
      );
      setSuccess(t(`备份恢复成功，已恢复 ${response.data.restoredCount} 个文档`, `Backup restored. ${response.data.restoredCount} documents restored.`));
      loadOverview();
      loadDocuments();
    } catch (err) {
      setError(t("恢复备份失败", "Failed to restore backup"));
    }
  };

  // 删除备份
  const deleteBackup = async (backupId: string) => {
    if (!window.confirm(t("确定要删除这个备份吗？此操作不可恢复。", "Delete this backup? This action cannot be undone."))) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/knowledge/backup/${backupId}`);
      setSuccess(t("备份已删除", "Backup deleted"));
      loadBackups();
    } catch (err) {
      setError(t("删除备份失败", "Failed to delete backup"));
    }
  };

  // 导出知识库
  const exportKnowledge = () => {
    window.open(`${API_BASE_URL}/api/knowledge/export`, "_blank");
  };

  // 获取文档详情
  const viewDocument = async (docId: string) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/knowledge/documents/${docId}`
      );
      setSelectedDoc(response.data);
      setEditableDoc(response.data);
    } catch (err) {
      setError(t("加载文档失败", "Failed to load document"));
    }
  };

  // 保存文档
  const saveDocument = async () => {
    if (!selectedDoc || !editableDoc) return;

    setSavingDoc(true);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/knowledge/documents/${selectedDoc.id}`,
        {
          title: editableDoc.title,
          category: editableDoc.category,
          content: editableDoc.content,
          metadata: editableDoc.metadata,
        }
      );

      setSelectedDoc(response.data.document);
      setEditableDoc(response.data.document);
      setSuccess(t("文档已保存", "Document saved"));
      loadOverview();
      loadDocuments();
      if (searchQuery.trim()) {
        searchKnowledge();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t("保存文档失败", "Failed to save document"));
    } finally {
      setSavingDoc(false);
    }
  };

  // 删除文档
  const deleteDocument = async () => {
    if (!selectedDoc) return;
    if (!window.confirm(t("确定要删除这个文档吗？此操作不可恢复。", "Delete this document? This action cannot be undone."))) {
      return;
    }

    try {
      await axios.delete(
        `${API_BASE_URL}/api/knowledge/documents/${selectedDoc.id}`
      );
      setSelectedDoc(null);
      setEditableDoc(null);
      setSuccess(t("文档已删除", "Document deleted"));
      loadOverview();
      loadDocuments();
      if (searchQuery.trim()) {
        searchKnowledge();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t("删除文档失败", "Failed to delete document"));
    }
  };

  // 上传完成回调
  const handleUploadComplete = (results: any) => {
    setSuccess(t(`成功上传 ${results.data?.length || 1} 个文件`, `Uploaded ${results.data?.length || 1} file(s) successfully`));
    loadOverview();
    loadDocuments();
    // 切换到概览标签页查看结果
    setActiveTab("overview");
  };

  useEffect(() => {
    loadOverview();
    loadBackups();
    loadDocuments();
  }, []);

  // 格式化文件大小
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  };

  // 格式化时间
  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString(language === "zh" ? "zh-CN" : "en-GB");
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header
        onClearChat={() => navigate("/")}
        onHome={() => navigate("/")}
        showNewChat={true}
      />

      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* 标题和统计卡片 */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t("知识库管理", "Knowledge Base")}
            </h1>
            <div className="flex gap-2">
              <button
                onClick={() => setShowImporter(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                title={t("导入知识库", "Import knowledge base")}
              >
                <FileJson className="w-4 h-4" />
                <span className="hidden sm:inline">{t("导入", "Import")}</span>
              </button>

              <button
                onClick={exportKnowledge}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                title={t("导出知识库", "Export knowledge base")}
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">{t("导出", "Export")}</span>
              </button>

              <button
                onClick={loadOverview}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                title={t("刷新", "Refresh")}
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">{t("刷新", "Refresh")}</span>
              </button>
            </div>
          </div>

          {/* 统计卡片 */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                <Database className="w-6 h-6 text-indigo-600 mb-2" />
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.totalDocuments}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {t("总文档数", "Total Documents")}
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                <Tag className="w-6 h-6 text-indigo-600 mb-2" />
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.categories.length}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {t("文档分类", "Categories")}
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                <FolderOpen className="w-6 h-6 text-indigo-600 mb-2" />
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.uploadedFiles.count}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {t("上传文件", "Uploaded Files")}
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                <Archive className="w-6 h-6 text-indigo-600 mb-2" />
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {backups.length}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {t("备份数量", "Backups")}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 标签页导航 */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="flex gap-2 overflow-x-auto pb-1">
            <button
              onClick={() => setActiveTab("overview")}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === "overview"
                  ? "bg-white dark:bg-gray-800 text-indigo-600 border-t border-l border-r border-gray-200 dark:border-gray-700"
                  : "text-gray-500 hover:text-gray-700 dark:text-gray-400"
              }`}
            >
              <PieChart className="w-4 h-4 inline mr-2" />
              {t("知识库概览", "Overview")}
            </button>
            <button
              onClick={() => {
                setActiveTab("documents");
                loadDocuments();
              }}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === "documents"
                  ? "bg-white dark:bg-gray-800 text-indigo-600 border-t border-l border-r border-gray-200 dark:border-gray-700"
                  : "text-gray-500 hover:text-gray-700 dark:text-gray-400"
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              {t("文档管理", "Documents")}
            </button>
            <button
              onClick={() => setActiveTab("upload")}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === "upload"
                  ? "bg-white dark:bg-gray-800 text-indigo-600 border-t border-l border-r border-gray-200 dark:border-gray-700"
                  : "text-gray-500 hover:text-gray-700 dark:text-gray-400"
              }`}
            >
              <Upload className="w-4 h-4 inline mr-2" />
              {t("上传文档", "Upload")}
            </button>
            <button
              onClick={() => setActiveTab("search")}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === "search"
                  ? "bg-white dark:bg-gray-800 text-indigo-600 border-t border-l border-r border-gray-200 dark:border-gray-700"
                  : "text-gray-500 hover:text-gray-700 dark:text-gray-400"
              }`}
            >
              <Search className="w-4 h-4 inline mr-2" />
              {t("搜索文档", "Search")}
            </button>
            <button
              onClick={() => setActiveTab("backup")}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === "backup"
                  ? "bg-white dark:bg-gray-800 text-indigo-600 border-t border-l border-r border-gray-200 dark:border-gray-700"
                  : "text-gray-500 hover:text-gray-700 dark:text-gray-400"
              }`}
            >
              <Archive className="w-4 h-4 inline mr-2" />
              {t("备份恢复", "Backups")}
            </button>
          </nav>
        </div>

        {/* 提示信息 */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-600 dark:text-red-400">
              {error}
            </span>
            <button onClick={() => setError(null)} className="ml-auto">
              <XCircle className="w-4 h-4 text-red-400" />
            </button>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
            <span className="text-sm text-green-600 dark:text-green-400">
              {success}
            </span>
            <button onClick={() => setSuccess(null)} className="ml-auto">
              <XCircle className="w-4 h-4 text-green-400" />
            </button>
          </div>
        )}

        {/* 标签页内容 */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          {/* 概览标签页 */}
          {activeTab === "overview" && stats && (
            <div className="space-y-6">
              {/* 分类统计 */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <Tag className="w-5 h-5 text-indigo-600" />
                  {t("文档分类统计", "Category Summary")}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(stats.categoryStats).map(
                    ([category, data]: [string, any]) => (
                      <div
                        key={category}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-4 h-4 text-indigo-600" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {category}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {data.count}
                          </span>
                          <span className="text-xs text-gray-500">{t("文档", "docs")}</span>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* 最近上传的文件 */}
              {stats.uploadedFiles.files.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-indigo-600" />
                    {t("最近上传的文件", "Recent Uploads")}
                  </h2>
                  <div className="space-y-2">
                    {stats.uploadedFiles.files
                      .slice(0, 5)
                      .map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {file.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>{formatSize(file.size)}</span>
                            <span>
                              {new Date(file.created).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* 最后更新时间 */}
              <div className="text-xs text-gray-500 text-right">
                {t("最后更新", "Last updated")}:{" "}
                {new Date(stats.lastUpdated).toLocaleString(language === "zh" ? "zh-CN" : "en-GB")}
              </div>
            </div>
          )}

          {/* 文档管理标签页 */}
          {activeTab === "documents" && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_220px_auto] gap-3">
                <input
                  type="text"
                  value={documentQuery}
                  onChange={(e) => setDocumentQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadDocuments()}
                  placeholder={t("搜索标题、分类或正文...", "Search title, category, or content...")}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                />
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">{t("全部分类", "All categories")}</option>
                  {stats?.categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
                <button
                  onClick={loadDocuments}
                  disabled={loading}
                  className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  {t("筛选", "Filter")}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t(`当前显示 ${documents.length} 个文档`, `Showing ${documents.length} document(s)`)}
                </p>
                <button
                  onClick={exportKnowledge}
                  className="text-sm text-indigo-600 dark:text-indigo-300 hover:underline flex items-center gap-1"
                >
                  <Download className="w-4 h-4" />
                  {t("下载完整知识库", "Download full knowledge base")}
                </button>
              </div>

              {documents.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">
                    {t("暂无匹配文档", "No matching documents")}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  {documents.map((doc) => (
                    <button
                      key={doc.id}
                      onClick={() => viewDocument(doc.id)}
                      className="w-full text-left p-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="font-medium text-gray-900 dark:text-white">
                              {doc.title}
                            </h3>
                            <span className="text-xs text-indigo-700 dark:text-indigo-200 bg-indigo-50 dark:bg-indigo-900/40 px-2 py-1 rounded">
                              {doc.category}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                            {doc.preview}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 上传标签页 */}
          {activeTab === "upload" && (
            <FileUploaderWithPreview
              onUploadComplete={handleUploadComplete}
              multiple={true}
            />
          )}

          {/* 搜索标签页 */}
          {activeTab === "search" && (
            <div className="space-y-6">
              {/* 搜索框 */}
              <div className="flex gap-3">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && searchKnowledge()}
                  placeholder={t("输入搜索关键词...", "Enter search keywords...")}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                />
                <button
                  onClick={searchKnowledge}
                  disabled={loading}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  {t("搜索", "Search")}
                </button>
              </div>

              {/* 搜索结果 */}
              {searchResults.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      {t(`找到 ${searchResults.length} 个相关文档`, `Found ${searchResults.length} matching document(s)`)}
                    </span>
                    <div className="flex gap-1 border border-gray-200 dark:border-gray-700 rounded-lg p-1">
                      <button
                        onClick={() => setViewMode("grid")}
                        className={`p-1.5 rounded ${
                          viewMode === "grid"
                            ? "bg-gray-100 dark:bg-gray-700"
                            : ""
                        }`}
                      >
                        <Grid className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setViewMode("list")}
                        className={`p-1.5 rounded ${
                          viewMode === "list"
                            ? "bg-gray-100 dark:bg-gray-700"
                            : ""
                        }`}
                      >
                        <List className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {viewMode === "grid" ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {searchResults.map((result, index) => (
                        <div
                          key={index}
                          className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => viewDocument(result.id)}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="font-medium text-gray-900 dark:text-white line-clamp-2">
                              {result.title}
                            </h3>
                            <span className="text-xs text-indigo-600 bg-indigo-100 dark:bg-indigo-900 px-2 py-1 rounded ml-2 flex-shrink-0">
                              {(result.score! * 100).toFixed(0)}%
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-3">
                            {result.preview}
                          </p>
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>{result.category}</span>
                            <Eye className="w-4 h-4" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {searchResults.map((result, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                          onClick={() => viewDocument(result.id)}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <FileText className="w-4 h-4 text-indigo-600" />
                              <span className="font-medium text-gray-900 dark:text-white">
                                {result.title}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-1">
                              {result.preview}
                            </p>
                          </div>
                          <div className="flex items-center gap-3 text-xs">
                            <span className="text-indigo-600 bg-indigo-100 dark:bg-indigo-900 px-2 py-1 rounded">
                              {(result.score! * 100).toFixed(0)}%
                            </span>
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 备份标签页 */}
          {activeTab === "backup" && (
            <div className="space-y-6">
              {/* 创建备份按钮 */}
              <div className="flex justify-end">
                <button
                  onClick={createBackup}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {t("创建新备份", "Create backup")}
                </button>
              </div>

              {/* 备份列表 */}
              {backups.length === 0 ? (
                <div className="text-center py-12">
                  <Archive className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">{t("暂无备份", "No backups yet")}</p>
                  <p className="text-sm text-gray-400 mt-1">
                    {t('点击"创建新备份"开始', 'Click "Create backup" to start')}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {backups.map((backup) => (
                    <div
                      key={backup.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <Archive className="w-5 h-5 text-indigo-600" />
                          <div>
                            <h3 className="font-medium text-gray-900 dark:text-white">
                              {backup.name}
                            </h3>
                            <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatTime(backup.createdAt)}
                              </span>
                              <span>
                                {t(`文档: ${backup.stats?.documentCount || 0}`, `Docs: ${backup.stats?.documentCount || 0}`)}
                              </span>
                              <span>{t("大小", "Size")}: {backup.sizeFormatted}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => restoreBackup(backup.id)}
                            className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                            title={t("恢复", "Restore")}
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteBackup(backup.id)}
                            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            title={t("删除", "Delete")}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {backup.stats?.categories &&
                        backup.stats.categories.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {backup.stats.categories
                              .slice(0, 5)
                              .map((cat, i) => (
                                <span
                                  key={i}
                                  className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-1 rounded"
                                >
                                  {cat}
                                </span>
                              ))}
                            {backup.stats.categories.length > 5 && (
                              <span className="text-xs text-gray-400">
                                +{backup.stats.categories.length - 5}
                              </span>
                            )}
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 导入弹窗 */}
        {showImporter && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="max-w-2xl w-full">
              <KnowledgeImporter
                onImportComplete={(stats) => {
                  setSuccess(t(`成功导入 ${stats.imported} 个文档`, `Imported ${stats.imported} document(s) successfully`));
                  loadOverview();
                  loadDocuments();
                  setShowImporter(false);
                }}
                onClose={() => setShowImporter(false)}
              />
            </div>
          </div>
        )}

        {/* 文档详情弹窗 */}
        {selectedDoc && editableDoc && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {t("编辑知识文档", "Edit Knowledge Document")}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {t("保存后会立即用于 AI 检索回答", "Saved changes are used by AI retrieval immediately.")}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSelectedDoc(null);
                    setEditableDoc(null);
                  }}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <XCircle className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-150px)] space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-4">
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      {t("标题", "Title")}
                    </span>
                    <input
                      type="text"
                      value={editableDoc.title}
                      onChange={(e) =>
                        setEditableDoc({ ...editableDoc, title: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      {t("分类", "Category")}
                    </span>
                    <input
                      type="text"
                      value={editableDoc.category}
                      onChange={(e) =>
                        setEditableDoc({
                          ...editableDoc,
                          category: e.target.value,
                        })
                      }
                      list="knowledge-categories"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                    />
                    <datalist id="knowledge-categories">
                      {stats?.categories.map((category) => (
                        <option key={category} value={category} />
                      ))}
                    </datalist>
                  </label>
                </div>

                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    {t("正文内容", "Content")}
                  </span>
                  <textarea
                    value={editableDoc.content}
                    onChange={(e) =>
                      setEditableDoc({ ...editableDoc, content: e.target.value })
                    }
                    rows={18}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white font-mono text-sm leading-relaxed"
                  />
                </label>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-500 dark:text-gray-400">
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                    {t("文档 ID", "Document ID")}: {selectedDoc.id}
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                    {t("字数", "Characters")}: {editableDoc.content.length}
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                    {t("更新时间", "Updated")}:{" "}
                    {selectedDoc.timestamp
                      ? new Date(selectedDoc.timestamp).toLocaleString()
                      : t("未知", "Unknown")}
                  </div>
                </div>
              </div>
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex flex-col sm:flex-row gap-2 sm:justify-between">
                <button
                  onClick={deleteDocument}
                  className="px-4 py-2 text-red-600 dark:text-red-300 border border-red-200 dark:border-red-900/60 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  {t("删除文档", "Delete Document")}
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSelectedDoc(null);
                      setEditableDoc(null);
                    }}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    {t("关闭", "Close")}
                  </button>
                  <button
                    onClick={saveDocument}
                    disabled={savingDoc}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {savingDoc ? t("保存中...", "Saving...") : t("保存", "Save")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeBasePage;
